import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Close as CloseIcon,
  ExitToApp as ExitToAppIcon,
  DirectionsCar as DirectionsCarIcon,
  PhotoCamera as PhotoCameraIcon,
  ImageNotSupported as ImageNotSupportedIcon,
  AttachMoney,
} from "@mui/icons-material";
import apiClient, { getApiBaseUrl } from "../../../utils/axios";
import { enqueueSnackbar } from "notistack";

const API_BASE = getApiBaseUrl();
const buildImageSrc = (value, tipo) => {
  if (!value) return null;
  if (value.startsWith('data:') || value.length > 500) return value;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  if (value.startsWith('/api/')) return `${API_BASE}${value}`;
  return `${API_BASE}/api/images/${tipo}/${value}`;
};


const VehicleExitDialog = ({ 
  open, 
  onClose, 
  vehicle, 
  onConfirmExit, 
  loading = false,
  loadVehicles
}) => {
  const [observations, setObservations] = useState("");
  const [error, setError] = useState("");
  const [config, setConfig] = useState({
    precioPorHora: 1,
    typePriceCalc: "hourly",
    fixedPrice: 0,
    lostTicketValue: 0,
    tiempoGratisHabilitado: false,
    minutosGratis: 0,
    moneda: "USD",
    minutosSalida: 15,
  });
  const [tarifaResuelta, setTarifaResuelta] = useState(null);
  const [showExcedidoAlert, setShowExcedidoAlert] = useState(false);
  const [excedidoData, setExcedidoData] = useState({
    minutosExcedidos: 0,
    montoAdicional: 0,
  });
  const [datosComprobante, setDatosComprobante] = useState({
    nombre: "",
    tipo_identificacion: "cedula",
    identificacion: "",
    correo: "",
    metodo: "efectivo",
    descripcion: "Pago Parqueadero Excedido",
  });


  // Resetear el estado cuando se abre/cierra el diálogo
  React.useEffect(() => {
    if (open) {
      setObservations("");
      setError("");
      setShowExcedidoAlert(false);
      setExcedidoData({ minutosExcedidos: 0, montoAdicional: 0 });
    }
  }, [open]);

  const obtenerDatosComprobante = async (placa) => {
    try {
      const response = await apiClient.get(`/api/vehiculos/comprobante/ultimo/${placa}`);
      
      if (response?.data?.success) {
        return response.data.data;
      }
    } catch (error) {
      console.error("Error obteniendo comprobante:", error);
      return null;
    }
  };

  useEffect(() => {
    const cargarConfiguracion = async () => {
      if (!open) return;
      try {
        const res = await apiClient.get("api/configuracion/cobros/parametros");
        let precioPorHora = 1;
        let typePriceCalc = "hourly";
        let fixedPrice = 0;
        if (res?.data?.success) {
          const minutosGratis =
            Number(res?.data?.data?.free_initial_time?.valor) || 0;
          const minutosSalida =
            Number(res?.data?.data?.exit_grace_minutes?.valor) || 15;
          precioPorHora = Number(res?.data?.data?.price_per_hour?.valor) || 1;
          typePriceCalc =
            res?.data?.data?.type_price_calc?.valor === "fixed"
              ? "fixed"
              : "hourly";
          fixedPrice = Number(res?.data?.data?.fixed_price?.valor) || 0;

          setConfig({
            tiempoGratisHabilitado:
              res?.data?.data?.is_free_time_enabled?.valor === "1",
            precioPorHora,
            typePriceCalc,
            fixedPrice,
            lostTicketValue: Number(res?.data?.data?.lost_ticket_value?.valor) || 0,
            minutosGratis: minutosGratis,
            minutosSalida: minutosSalida,
            moneda: res?.data?.data?.currency?.valor || "USD",
          });
        }

        // Resolver si el tipo de vehículo cae en un grupo de tarifa —
        // esa tarifa gana sobre la global para el cobro de excedido.
        setTarifaResuelta(null);
        if (vehicle?.codigo_qr) {
          try {
            const params = new URLSearchParams({
              precio_por_hora: precioPorHora,
              type_price_calc: typePriceCalc,
              fixed_price: fixedPrice,
              minutos_gratis: 0,
              minutos_gratis_habilitados: "0",
            });
            const resTarifa = await apiClient.get(
              `api/vehiculos/calcular-cobro/${vehicle.codigo_qr}?${params}`,
            );
            if (resTarifa?.data?.success) {
              setTarifaResuelta(resTarifa.data.data);
            }
          } catch (e) {
            console.error("Error resolviendo tarifa por tipo de vehículo:", e);
          }
        }

        if (vehicle?.numero_placa) {
          const datosComprobante = await obtenerDatosComprobante(vehicle.numero_placa);
          if (datosComprobante) {
            setDatosComprobante({
              nombre: datosComprobante.nombre,
              tipo_identificacion: datosComprobante.tipo_identificacion,
              identificacion: datosComprobante.identificacion,
              correo: datosComprobante.correo,
              metodo: datosComprobante.metodo,
            });
          }
        }
      } catch (error) {
        console.error("Error al cargar configuración:", error);
        setError("Error al cargar la configuración de cobros");
      }
    };

    cargarConfiguracion();
  }, [open]);

  // Tick que fuerza re-render cada 30 s mientras el diálogo está abierto,
  // para que el estado "excedido" se actualice sin reabrir el diálogo.
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, [open]);

  const calcularExcesoYValor = (fechaPago) => {
    if (!fechaPago)
      return { excedido: false, minutosExcedidos: 0, montoAdicional: 0 };

    // Parsear directamente — NO hacer conversión UTC→local.
    // La conversión anterior desplazaba la fecha +5 h (UTC-5 Ecuador),
    // haciendo que fecha_pago apareciera en el futuro y excedido = false siempre.
    const fechaPagoDate = new Date(fechaPago);
    const ahora = new Date();
    const minutosDesdeElPago = Math.floor(
      (ahora - fechaPagoDate) / (1000 * 60),
    );

    const minutosExcedidos = minutosDesdeElPago - config.minutosSalida;
    if (minutosExcedidos > 0) {
      let montoAdicional = 0;
      const typePriceCalcEfectivo = tarifaResuelta?.type_price_calc ?? config.typePriceCalc;
      const fixedPriceEfectivo = tarifaResuelta?.fixed_price ?? config.fixedPrice;
      const precioPorHoraEfectivo = tarifaResuelta?.precio_por_hora ?? config.precioPorHora;

      if (typePriceCalcEfectivo === "fixed") {
        montoAdicional = Number(fixedPriceEfectivo || 0);
      } else {
        const horasExcedidas = minutosExcedidos / 60;
        const horasRedondeadas = Math.ceil(horasExcedidas);
        montoAdicional = horasRedondeadas * precioPorHoraEfectivo;
      }

      return {
        excedido: true,
        minutosExcedidos,
        montoAdicional,
      };
    }

    return { excedido: false, minutosExcedidos: 0, montoAdicional: 0 };
  };

  const handleSubmit = () => {
    // Validación básica
    if (observations.trim().length > 500) {
      setError("Las observaciones no pueden exceder 500 caracteres");
      return;
    }

    if (!vehicle.pagado) {
      setError(
        "El vehículo debe estar pagado antes de poder registrar la salida",
      );
      return;
    }

    onConfirmExit(vehicle?.codigo_qr, observations.trim());
  };

  const handleContinuePay = () => {
    const resultadoExceso = calcularExcesoYValor(vehicle.fecha_pago);
    if (resultadoExceso.excedido) {
      setExcedidoData({
        minutosExcedidos: resultadoExceso.minutosExcedidos,
        montoAdicional: resultadoExceso.montoAdicional,
      });
      setShowExcedidoAlert(true);
    }
  };

  const handleConfirmPago = async () => {
    try {
      const payload = {
        codigo_qr: vehicle?.codigo_qr,
        facturas: [],
        tipo: "excedido",
        horas_permanencia: excedidoData.montoAdicional.toFixed(2),
        horas_descontadas: 0,
        horas_cobradas: excedidoData.montoAdicional.toFixed(2),
        total_pagar: excedidoData.montoAdicional.toFixed(2),
        type_price_calc: tarifaResuelta?.type_price_calc ?? config.typePriceCalc,
        fixed_price: tarifaResuelta?.fixed_price ?? config.fixedPrice,
        datos_comprobante: datosComprobante,
      };
      await apiClient.post(`/api/vehiculos/cobrar`, payload);

      enqueueSnackbar("Pago registrado, el vehículo puede salir.", {
        variant: "success",
        autoHideDuration: 5000,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
      });

      if (loadVehicles) {
        await loadVehicles(1, false);
      }

      setShowExcedidoAlert(false);

      onClose();
    } catch (error) {
      console.error("Error al procesar el pago:", error);
      setError(error.response?.data?.message || "Error al procesar el pago");
    }
  };

  const handleCancelExcedidoAlert = () => {
    setShowExcedidoAlert(false);
  };

  const handleClose = () => {
    if (!loading) {
      setObservations("");
      setError("");
      onClose();
    }
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return '';
    return new Date(dateTime).toLocaleString('es-EC', {
      timeZone: 'America/Guayaquil',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  if (!vehicle) return null;

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header del diálogo */}
      <DialogTitle
        sx={{
          backgroundColor: "#393939",
          color: "white",
          p: 0,
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                borderRadius: 2,
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ExitToAppIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="700">
                Registrar Salida
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Confirmar salida del vehículo
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={loading}
            sx={{
              color: "white",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
              borderRadius: 2,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Contenido del diálogo */}
      <DialogContent sx={{ p: 0, backgroundColor: "#f8fafc" }}>
        <Box sx={{ p: 3 }}>
          {/* Información del vehículo */}
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 3,
              p: 3,
              mb: 3,
              border: "1px solid #e2e8f0",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            }}
          >
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <DirectionsCarIcon sx={{ color: "#64748b", fontSize: 24 }} />
              <Typography variant="h6" fontWeight="600" color="text.primary">
                Información del Vehículo
              </Typography>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  PLACA
                </Typography>
                <Typography variant="body1" fontWeight="700" sx={{ mt: 0.5 }}>
                  {vehicle.numero_placa || vehicle.numero_placa_auxiliar}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  COLOR
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {vehicle.color_vehiculo || "No especificado"}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  TIPO
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {vehicle.tipo_vehiculo_label || vehicle.tipo_vehiculo || "No especificado"}
                </Typography>
              </Box>

              <Box >
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  FECHA DE ENTRADA
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {formatDateTime(vehicle.fecha_entrada)}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="600"
                >
                  PAGO
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={vehicle.pagado ? "Pagado" : "Pendiente"}
                      size="small"
                      sx={{
                        backgroundColor: vehicle.pagado ? "#22c55e" : "#f59e0b",
                        color: "white",
                        fontWeight: "600",
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="600"
                    >
                       {calcularExcesoYValor(vehicle.fecha_pago)?.excedido &&
                        " - Excedido del tiempo de salida"}
                    </Typography>
                  </Box>
                </Box>

              <Box >
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  ESTADO ACTUAL
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label="En el parqueadero"
                    size="small"
                    sx={{
                      backgroundColor: "#22c55e",
                      color: "white",
                      fontWeight: "600",
                    }}
                  />
                </Box>
              </Box>

              {/* Imagen de entrada */}
              {vehicle.imagen_entrada && (
                <Box sx={{ gridColumn: "-1 / 1" }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">
                    IMAGEN DE ENTRADA
                  </Typography>
                  <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        position: "relative",
                        width: 120,
                        height: 80,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "2px solid #e5e7eb",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <img
                        src={buildImageSrc(vehicle.imagen_entrada, 'entrada')}
                        alt="Imagen de entrada"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      {/* Fallback para imagen que no carga */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          display: 'none',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f3f4f6',
                          color: '#6b7280'
                        }}
                      >
                        <ImageNotSupportedIcon sx={{ fontSize: 24 }} />
                      </Box>
                      {/* Badge de entrada */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          backgroundColor: "rgba(34, 197, 94, 0.9)",
                          color: "white",
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          fontSize: "0.625rem",
                          fontWeight: "600",
                          backdropFilter: "blur(4px)",
                        }}
                      >
                        ENTRADA
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Campo de observaciones */}
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 3,
              p: 3,
              border: "1px solid #e2e8f0",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            }}
          >
            <Typography variant="h6" fontWeight="600" color="text.primary" mb={2}>
              Observaciones de Salida
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Ingrese observaciones sobre el estado del vehículo, daños observados, o cualquier comentario relevante..."
              value={observations}
              onChange={(e) => {
                setObservations(e.target.value);
                if (error) setError("");
              }}
              disabled={loading}
              error={!!error}
              helperText={
                error || 
                `${observations.length}/500 caracteres${observations.length === 0 ? " (opcional)" : ""}`
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#fafafa",
                  "& fieldset": {
                    borderColor: "#e2e8f0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#cbd5e1",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#ef4444",
                    borderWidth: 2,
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  fontSize: "0.875rem",
                  color: "#9ca3af",
                },
              }}
            />
          </Box>

          {/* Alerta informativa */}
          <Alert 
            severity="info" 
            sx={{ 
              mt: 2, 
              borderRadius: 2,
              backgroundColor: "#dbeafe",
              border: "1px solid #bfdbfe",
              "& .MuiAlert-icon": {
                color: "#3b82f6",
              },
            }}
          >
            <Typography variant="body2">
              <strong>Importante:</strong> Una vez confirmada la salida, se registrará la fecha y hora actual automáticamente. 
              Esta acción no se puede deshacer.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      {/* Footer del diálogo */}
      <DialogActions
        sx={{
          p: 3,
          backgroundColor: "white",
          borderTop: "1px solid #e2e8f0",
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={loading}
          sx={{
            borderColor: "#d1d5db",
            color: "#374151",
            "&:hover": {
              borderColor: "#9ca3af",
              backgroundColor: "#f9fafb",
            },
            borderRadius: 2,
            px: 3,
            py: 1,
            textTransform: "none",
            fontWeight: "600",
          }}
        >
          Cancelar
        </Button>

          {calcularExcesoYValor(vehicle.fecha_pago).excedido && (
            <Button
              variant="contained"
              onClick={handleContinuePay}
              disabled={
                loading || !calcularExcesoYValor(vehicle.fecha_pago).excedido
              }
              startIcon={
                loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <AttachMoney />
                )
              }
              sx={{
                backgroundColor: "#2e7d32",
                color: "white",
                "&:hover": {
                  backgroundColor: "#1b5e20",
                },
                "&:disabled": {
                  backgroundColor: "#b6d0b6",
                  color: "white",
                },
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: "none",
                fontWeight: "600",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              }}
            >
              Continuar al pago
            </Button>
          )}

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || calcularExcesoYValor(vehicle.fecha_pago).excedido}
          startIcon={
            loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <ExitToAppIcon />
            )
          }
          sx={{
            backgroundColor: "#ee3b3b",
            color: "white",
            "&:hover": {
              backgroundColor: "#dc2626",
            },
            "&:disabled": {
              backgroundColor: "#fca5a5",
              color: "white",
            },
            borderRadius: 2,
            px: 3,
            py: 1,
            textTransform: "none",
            fontWeight: "600",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          }}
        >
          {loading ? "Registrando..." : "Confirmar Salida"}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
        open={showExcedidoAlert}
        onClose={handleCancelExcedidoAlert}
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            backgroundColor: "#f59e0b",
            color: "white",
          }}
        >
          <Typography variant="h5" fontWeight="700">
            Tiempo de Gracia Excedido
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 3 }}>
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                backgroundColor: "#fef3c7",
              }}
            >
              <Typography variant="body2">
                El vehículo ha excedido el tiempo de gracia de{" "}
                {config.minutosSalida} minutos después del pago.
              </Typography>
            </Alert>
            <Box
              sx={{
                backgroundColor: "#f8fafc",
                p: 3,
                border: "1px solid #e2e8f0",
              }}
            >
              <Typography
                variant="h6"
                fontWeight="600"
                color="text.primary"
                mb={2}
              >
                Detalle
              </Typography>
              <Box sx={{ display: "grid", gap: 2 }}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#f8fafc",
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      color="text.secondary"
                    >
                      Fecha y hora de pago:
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="700"
                      color="text.secondary"
                    >
                      {formatDateTime(vehicle?.fecha_pago)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      color="text.secondary"
                    >
                      Tiempo de Gracia Permitido:
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="700"
                      color="text.secondary"
                    >
                      {config.minutosSalida} minutos
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      color="text.secondary"
                    >
                      Minutos Excedidos:
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="700"
                      color="text.secondary"
                    >
                      {excedidoData.minutosExcedidos} minutos
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      color="text.secondary"
                    >
                      {(tarifaResuelta?.type_price_calc ?? config.typePriceCalc) === "fixed"
                        ? "Tarifa Fija:"
                        : "Tarifa por Hora:"}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="700"
                      color="text.secondary"
                    >
                      {config.moneda} {(tarifaResuelta?.type_price_calc === "fixed"
                        ? (tarifaResuelta?.fixed_price ?? config.fixedPrice)
                        : (tarifaResuelta?.precio_por_hora ?? config.precioPorHora)
                      ).toFixed(2)}
                      {tarifaResuelta?.tarifa_aplicada?.origen === "grupo" &&
                        ` (${tarifaResuelta.tarifa_aplicada.grupo_nombre})`}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    border: "1px solid #ef4444",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" fontWeight="700">
                    Monto Adicional a Pagar:
                  </Typography>
                  <Typography variant="h4" fontWeight="700">
                    {config.moneda} {excedidoData.montoAdicional.toFixed(2)}
                  </Typography>
                </Box>

                {/* Selector de método de pago */}
                <FormControl fullWidth size="small">
                  <InputLabel>Método de Pago</InputLabel>
                  <Select
                    value={datosComprobante.metodo}
                    label="Método de Pago"
                    onChange={(e) =>
                      setDatosComprobante((prev) => ({ ...prev, metodo: e.target.value }))
                    }
                  >
                    <MenuItem value="efectivo">Efectivo</MenuItem>
                    <MenuItem value="tarjeta">Tarjeta</MenuItem>
                    <MenuItem value="transferencia">Transferencia</MenuItem>
                    <MenuItem value="de_una">De Una</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleCancelExcedidoAlert}
            disabled={loading}
            sx={{
              color: "#374151",
              borderColor: "#374151",
              borderRadius: 2,
              px: 3,
              py: 1,
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleConfirmPago}
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <ExitToAppIcon />
              )
            }
            sx={{
              backgroundColor: "#22c55e",
              color: "white",
              "&:hover": {
                backgroundColor: "#16a34a",
              },
              "&:disabled": {
                backgroundColor: "#86efac",
                color: "white",
              },
              borderRadius: 2,
              px: 3,
              py: 1,
            }}
          >
            {loading ? "Procesando..." : "Confirmar Pago"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VehicleExitDialog;